import type { LoaderFunctionArgs } from '~/types/route-args';
import type { SerializeFrom } from '~/types/serialize-from';
import { useRef, useEffect, useLayoutEffect, useState, useMemo } from 'react';
import {
    data as routerData,
    useFetcher,
    useLoaderData,
    useNavigate,
    useNavigation,
    useRevalidator,
    useSearchParams,
    type ClientLoaderFunctionArgs,
    type ShouldRevalidateFunction,
} from 'react-router';
import { redirect } from 'react-router';
import { useAtom } from 'jotai';
import { useTracker } from '@archipro-website/tracker';
import { RESET_AUTO_FILTERS_ACTION } from '~/routes/actions.search.reset-auto-filters';
import { getSearchPaginatedResults } from '@modules-rocco/search/api/get-search-paginated-results.server';
import { mapURLToFilterStates } from '@modules-rocco/filters/data-mappers/url-to-filter-state';
import { parseURL } from '@modules-rocco/url/api/parse-url';
import { generateURL } from '@modules-rocco/url/api/generate-url';
import { getSearchAggregations } from '@modules-rocco/search/api/get-search-aggregations';
import { setFilterStatesAggregations } from '@modules-rocco/filters/api/create-filter-states';
import { getPermissions } from '~/modules/user';
import { PermissionEnum } from 'generated/graphql';
import { getAllTileFromESSearchResult } from '@modules-rocco/search/data-mappers/tile-search-result';
import { attachMixedTileWebsites } from '~/modules/directory/util/attach-tile-websites.server';
import { collectFallibleResources } from '@modules-rocco/root/api/collect-fallible-resources.server';
import { mapURLToSortOption } from '@modules-rocco/filters/data-mappers/url-to-sort-option';
import { DEFAULT_PAGE_SIZE } from '@modules-rocco/search/config/search-service.config';
import { useSearchInput } from '@rocco/ui/search/hooks/use-search-input';
import { SearchBar } from '@rocco/ui/search/components/SearchBar';
import {
    formatNumberWithCommas,
    roundToNearest,
} from '@rocco/utils/format-number';
import { mapSearchIndexToFilterTabsViewModel } from '@modules-rocco/search/data-mappers/search-index-to-filter-tabs';
import { SearchSortOptions } from '@modules-rocco/search/types/enum/search-sort-options';
import { getSearchSuggestions } from '@modules-rocco/search/api/get-search-suggestions.server';
import { useCategoryFilterSort } from '@ui-rocco/hooks/use-category-filter-sort';
import { useRecentlySearched } from '@modules-rocco/search/hook/use-recently-searched';
import type { SearchSuggestion } from '@rocco/ui/search/view-models/search-suggestion';
import { SearchIndexes } from '@rocco/types/enum/search-indexes';
import { getSearchResultGroups } from '@modules-rocco/search/api/get-search-result-groups.server';
import { getRouteData } from '~/modules/root/util/get-route-data';
import { ClientOnly } from 'remix-utils';
import { uiStateAtomSearchClicked } from '@rocco/ui/website-header/states/ui-states-search-clicked';
import { getActiveFiltersCount } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModal';
import { useSearchTracking } from '@ui-rocco/hooks/use-search-tracking';
import { getURLPaginationState } from '~/modules-rocco/pagination/util/create-pagination-state.server';
import { useSearchQuery } from '@ui-rocco/hooks/use-search-query';
import { searchPageDataAtom } from '~/modules-rocco/search/states/search-page-data-atom';
import { SearchPageContentSectionsDesktop } from '~/modules-rocco/search/components/SearchPageContentSectionsDesktop';
import { SearchPageContentSectionsMobile } from '~/modules-rocco/search/components/SearchPageContentSectionsMobile';
import {
    getCanonicalHreflang,
    createMetaTitle,
    createOGTags,
    createMetaDescription,
    mergeMeta,
} from '~/modules/seo';
import { getFilterSettings } from '~/modules-rocco/search/util/get-filter-settings';
import {
    hasFeature,
    useRootData,
    useStatsNumbers,
    isUsProductLaunchEnabled,
} from '~/modules/root';
import { getSession } from '~/modules/root/util/session.server';
import { useSearch } from '~/modules/search/hook/use-search';
import {
    getSearchClassification,
    mapSearchClassificationToFilters,
} from '~/modules/search/api/search-classification.server';
import { getHiddenFacets } from '~/modules/product/api/get-facets.server';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';
import { TileAppModeScope } from '@rocco/contexts/TileAppModeScopeContext';
import { trackSearchResults } from '~/modules/tracking/util/track-search-results';

/**
 * Loads the search results for the search page. When making changes to this
 * function, make sure this runs as fast as possible. We want to minimize the
 * search time to improve the user experience.
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const { logger, appDisplayMode, user, featureFlags, localisation } =
        context;
    const session = await getSession(request);
    const url = new URL(request.url);
    const urlInfo = parseURL(url.pathname + url.search, featureFlags);
    const searchParams = url.searchParams;
    const category = urlInfo.params.category?.value;
    const showProductSearchCategory = isUsProductLaunchEnabled(
        localisation.countryConfig,
        featureFlags,
    );

    if (!showProductSearchCategory && category === 'products') {
        const searchQuery = searchParams.get('search') ?? '';
        const redirectUrl = generateURL(
            '/search',
            searchQuery ? { search: searchQuery } : {},
            true,
            featureFlags,
        );
        return redirect(redirectUrl, { headers: await session.getHeaders() });
    }

    const filterSettings = getFilterSettings(category);
    const urlFilterStates = mapURLToFilterStates(
        filterSettings?.config ?? null,
        urlInfo,
        'search',
    );
    const searchQuery = urlFilterStates.states.search?.query ?? '';
    const { page } = getURLPaginationState(searchParams, DEFAULT_PAGE_SIZE);
    const autoSearchFilters = session.getSearchFilters();

    if (
        searchQuery &&
        autoSearchFilters?.originalQuery !== searchQuery &&
        autoSearchFilters?.modifiedQuery !== searchQuery &&
        !category &&
        getActiveFiltersCount(urlFilterStates) === 0 &&
        page <= 1
    ) {
        // If performing a fresh search with no filters, attempt to auto-generate filters.
        const searchClassification = await getSearchClassification({
            context,
            searchQuery,
        });
        if (searchClassification) {
            const autoFilters = mapSearchClassificationToFilters(
                searchQuery,
                searchClassification,
            );
            session.setSearchFilters(autoFilters);
            if (autoFilters.modifiedQuery || autoFilters.category) {
                const autoFilterCategory =
                    autoFilters.category === 'products' &&
                    !showProductSearchCategory
                        ? undefined
                        : autoFilters.category;
                const url = generateURL(
                    '/search',
                    {
                        search: autoFilters.modifiedQuery || searchQuery,
                        ...(autoFilterCategory
                            ? { category: autoFilterCategory }
                            : {}),
                        ...(autoFilters.color
                            ? { colors: [autoFilters.color] }
                            : {}),
                    },
                    true,
                    featureFlags,
                );
                return redirect(url, { headers: await session.getHeaders() });
            }
        }
    }

    // Only show "auto-filter" message to user once per unique query.
    if (
        autoSearchFilters?.originalQuery === searchQuery ||
        autoSearchFilters?.modifiedQuery === searchQuery
    ) {
        // Make sure users can search for the original query if it was modified.
        session.setSearchFiltersApplied();
    } else {
        // Only attempt to auto-generate filters the first time a query is searched.
        session.setSearchFilters({
            originalQuery: searchQuery,
            isApplied: true,
        });
    }

    const sortOption = mapURLToSortOption(
        urlInfo,
        urlFilterStates.states.search?.query
            ? undefined
            : SearchSortOptions.Featured,
    );
    const facetFilterFeatureEnabled = hasFeature(featureFlags, 'filter_facet');

    // The result set is the page, so its Search Service failure is the
    // page's own 503 and stays outside the fallible collection. The panels
    // beside it (facets, suggestions, mobile groups) still degrade quietly.
    const [
        searchQueryResponse,
        [aggregations, suggestedResults, searchResultGroups, hiddenFacets],
    ] = await Promise.all([
        getSearchPaginatedResults(
            context,
            request,
            urlFilterStates.states.search?.searchIndex,
            urlFilterStates,
            DEFAULT_PAGE_SIZE,
            sortOption,
        ),
        collectFallibleResources(
            [
                getSearchAggregations(
                    context,
                    urlFilterStates.states.search?.searchIndex,
                    urlFilterStates,
                ),
                getSearchSuggestions(context, {
                    search: searchQuery,
                    pageSize: 15,
                }),
                appDisplayMode === 'desktop'
                    ? null
                    : getSearchResultGroups(
                          context,
                          request,
                          [
                              ...(showProductSearchCategory
                                  ? [SearchIndexes.Products]
                                  : []),
                              SearchIndexes.Professionals,
                              SearchIndexes.Projects,
                              SearchIndexes.Articles,
                          ],
                          urlFilterStates,
                          DEFAULT_PAGE_SIZE,
                          sortOption,
                          5, // limit to 5 hits per group
                      ),
                urlFilterStates.states.search?.searchIndex === 'product' &&
                facetFilterFeatureEnabled
                    ? getHiddenFacets({ context, user })
                    : null,
            ],
            logger,
        ),
    ]);

    const searchResultGroupsTiles =
        searchResultGroups?.map((group) => {
            return {
                index: group.index,
                tiles: getAllTileFromESSearchResult(
                    group.searchResponse.searchResults,
                    user,
                ),
            };
        }) ?? [];

    if (searchQueryResponse.pagination.isPageOutOfRange) {
        searchParams.delete('page');
        return redirect(`${url.pathname}?${searchParams.toString()}`, 302);
    }

    // update filterStates with aggregations
    const filterStates = setFilterStatesAggregations({
        searchIndex: urlFilterStates.states.search?.searchIndex,
        aggregations: aggregations ?? {},
        defaultSort: sortOption,
        filterStates: urlFilterStates,
        facetFilterFeatureEnabled,
        hasMarketingView: getPermissions(user, PermissionEnum.BmMarketingView),
        hiddenFacetIds: hiddenFacets?.map((facet) => facet.ID),
    });

    const totalCount = searchQueryResponse.pagination.totalCount;
    const filterTabs = mapSearchIndexToFilterTabsViewModel(
        url,
        appDisplayMode === 'mobile' ? 'All' : undefined,
        { showProductCategory: showProductSearchCategory },
    );

    // The search index carries no website field, so the tiles' "Visit site" is
    // looked up separately. Every tile on the page goes in as one list — the
    // grid and the grouped rails together — so the whole page costs one call
    // per entity type it actually shows, rather than one per rail. The lookup
    // preserves order and length, which is what lets the answer be cut back
    // into the lists it came from. A failed lookup drops the action, not the
    // page.
    const matchedTiles = getAllTileFromESSearchResult(
        searchQueryResponse.searchResults,
        user,
    );
    const withWebsites = await attachMixedTileWebsites(
        [
            ...matchedTiles,
            ...searchResultGroupsTiles.flatMap((group) => group.tiles),
        ],
        context,
    );

    const results = withWebsites.slice(0, matchedTiles.length);
    let groupStart = matchedTiles.length;
    const groupedTiles = searchResultGroupsTiles.map((group) => {
        const tiles = withWebsites.slice(
            groupStart,
            groupStart + group.tiles.length,
        );
        groupStart += group.tiles.length;
        return { ...group, tiles };
    });

    return routerData(
        {
            filterStates,
            searchQuery,
            searchIndex: urlFilterStates.states.search?.searchIndex,
            pagination: searchQueryResponse.pagination,
            results,
            totalCount,
            filterTabs,
            suggestedCategories: suggestedResults?.categories ?? [],
            searchResultGroups: groupedTiles,
            autoSearchFilters:
                autoSearchFilters && !autoSearchFilters.isApplied
                    ? autoSearchFilters
                    : null,
            isDataLoaded: true as const,
        },
        {
            headers: await session.getHeaders({
                'Cache-Control': 'no-cache',
            }),
        },
    );
};

export const meta = mergeMeta<typeof loader>(
    () => {
        // Initial static meta tags, if any, or just an empty array
        return [];
    },
    (props) => {
        const { matches, location } = props;
        const { rootData } = getRouteData(matches) ?? { rootData: {} };
        const stage = rootData?.env?.stage;
        const data = props.data as SerializeFrom<typeof clientLoader>;

        // searchQuery can be taken from data (loader) or directly from location for more safety
        const searchParams = new URLSearchParams(location.search);
        const searchQueryFromURL = searchParams.get('search');
        const effectiveSearchQuery =
            data?.searchQuery ?? searchQueryFromURL ?? '';

        const title = createMetaTitle(props, [effectiveSearchQuery, 'Search']);
        const description = createMetaDescription([]);
        const ogTags = createOGTags({
            meta: props,
            title,
            description,
        });
        const canonicals = getCanonicalHreflang(props);
        return [
            { title },
            { name: 'description', content: description },
            ...ogTags,
            ...canonicals,
            ...(stage === 'Production'
                ? [{ name: 'robots', content: 'noindex' }]
                : []),
        ];
    },
);

// Use module-scoped variable to track if the page was navigated to from another page.
let isNavigated = true;

/**
 * Use `clientLoader` to speed up navigation to the search page.
 * @see https://remix.run/docs/en/main/route/client-loader
 */
export const clientLoader = async ({
    serverLoader,
    request,
}: ClientLoaderFunctionArgs) => {
    if (isNavigated) {
        // Do not hit the server if navigated from another page. Fetch search
        // results from the client after page load.
        isNavigated = false;
        const url = new URL(request.url);
        const searchQuery = url.searchParams.get('search')?.trim() ?? '';

        return {
            searchQuery,
            isDataLoaded: false as const,
        };
    }

    return await serverLoader<typeof loader>();
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
    defaultShouldRevalidate,
    formAction,
}) => {
    // Do not revalidate loader on form actions. Only revalidate on URL and
    // URL search param changes.
    if (formAction) {
        return false;
    }
    return defaultShouldRevalidate;
};

type MobileSearchPageView = 'results' | 'recentlySearched' | 'suggestions';

/**
 * Search is a listing surface like the category and browse pages, so its
 * tiles open detail pages in the mobile app-mode presentation. The hero morph
 * is not scoped here — it runs wherever a tile does.
 */
export default function SearchPageWithTileAppMode() {
    return (
        <TileAppModeScope>
            <SearchPage />
        </TileAppModeScope>
    );
}

function SearchPage() {
    const loaderData = useLoaderData<typeof clientLoader>();
    const { appDisplayMode } = useRootData();
    const revalidator = useRevalidator();
    const navigation = useNavigation();
    const autoFilterResetFetcher = useFetcher();
    const tracker = useTracker();
    const [pageData, setAtomPageData] = useAtom(searchPageDataAtom);
    const [urlSearchParams] = useSearchParams();
    const queryDirectlyFromUrl = urlSearchParams.get('search') ?? '';
    const { isSearching } = useSearch();

    useEffect(() => {
        isNavigated = false;
        return () => {
            // Reset search data when navigating to another page.
            setAtomPageData(null);
            // Reset flag to track if the page was navigated to from another page.
            isNavigated = true;
            autoFilterResetFetcher.load(RESET_AUTO_FILTERS_ACTION);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setAtomPageData]);

    /*
     * Fetches search results when URL (search params or pathname) changes.
     *
     * A layout effect, not a passive one. Every component below reads the
     * results off the atom rather than off the loader, and an in-route
     * navigation never unmounts this route — so with a passive effect React
     * commits and paints the new URL against the previous page's atom first:
     * old tiles, old highlighted pill, old result count. A layout effect runs
     * before that paint, so the stale frame is never shown.
     */
    useLayoutEffect(() => {
        if (loaderData.isDataLoaded) {
            // The page load is initiated by the user directly. Jotai bails out
            // when the value is unchanged, so re-writing the same loader data
            // does not re-render subscribers.
            setAtomPageData(loaderData);
            return;
        }
        if (!pageData && revalidator.state === 'idle') {
            // Client-side search if navigated from another page.
            revalidator.revalidate();
        }
    }, [pageData, setAtomPageData, loaderData, revalidator]);

    /*
     * The skeleton has to cover the swap, not just the first load. `!pageData`
     * alone is false throughout an in-route navigation — the atom still holds
     * the previous page — so the old results stayed on screen as live content
     * while the new ones were in flight.
     */
    const isResultsLoading = !pageData || navigation.state === 'loading';

    const searchIndex = pageData?.searchIndex;
    const filterStates = pageData?.filterStates;

    const { statsNumbers } = useStatsNumbers();
    const trackingConfig = useSearchTracking();
    const lastSearchResultsTrackingKeyRef = useRef<string | null>(null);
    const memoizedEmptyFiltersConfig = useMemo(
        () => ({
            resultName: '',
            barFilters: [],
            modalFilters: [],
        }),
        [],
    );
    const memoizedEmptyFilterStates = useMemo(
        () => ({ visibilityType: 'search' as const, states: {} }),
        [],
    );

    const filterSettings = getFilterSettings(searchIndex);
    const filterSortProps = useCategoryFilterSort(
        filterSettings ? filterSettings.config : memoizedEmptyFiltersConfig,
        filterSettings?.index,
        filterStates ?? memoizedEmptyFilterStates,
    );

    const { addRecentSearch, recentlySearched, clearHistory } =
        useRecentlySearched();

    const queryForSearchHook = useSearchQuery(queryDirectlyFromUrl);

    const searchInput = useSearchInput({
        query: queryForSearchHook,
        searchIndex: searchIndex,
        doSearch: (finalQuery) => {
            if (finalQuery) {
                addRecentSearch(finalQuery);
                navigate(
                    generateURL('/search', {
                        search: finalQuery,
                    }),
                );
            } else {
                navigate('/search');
            }
        },
    });

    const {
        searchQuery: inputSearchQuery,
        handleInputChange,
        handleKeyPress,
        handleSearch,
        suggestionGroups,
        clearSearchQuery,
    } = searchInput;

    const hasSuggestions = suggestionGroups.some(
        (group) => group.options.length > 0,
    );

    const [view, setView] = useState<MobileSearchPageView>(
        queryDirectlyFromUrl ? 'results' : 'recentlySearched',
    );

    // Determines the mobile view (results, suggestions, or recently searched) based on query and suggestion state.
    useEffect(() => {
        const showSuggestions =
            inputSearchQuery !== queryDirectlyFromUrl && hasSuggestions;
        const showRecentSearches =
            !queryDirectlyFromUrl && !inputSearchQuery && !hasSuggestions;

        setView(
            showSuggestions
                ? 'suggestions'
                : showRecentSearches
                  ? 'recentlySearched'
                  : 'results',
        );
    }, [queryDirectlyFromUrl, inputSearchQuery, hasSuggestions]);

    const isMobile = appDisplayMode === 'mobile';

    const shouldShowSearchBar = true;

    const navigate = useNavigate();
    const [searchIconClicked, setSearchIconClicked] = useAtom(
        uiStateAtomSearchClicked,
    );

    const searchSectionInputRef = useRef<HTMLInputElement>(null);
    // Focuses the search input when the header search icon is clicked.
    useEffect(() => {
        if (searchIconClicked) {
            searchSectionInputRef.current?.focus();
            setSearchIconClicked(false);
        }
    }, [searchIconClicked, setSearchIconClicked]);

    const placeholderTexts = useMemo(() => {
        if (
            !statsNumbers?.ProductCount ||
            !statsNumbers?.ProfessionalCount ||
            !statsNumbers?.ProjectCount
        ) {
            return ['Search'];
        }
        return [
            `Search ${formatNumberWithCommas(roundToNearest(statsNumbers.ProductCount))}+ products`,
            `Search ${formatNumberWithCommas(roundToNearest(statsNumbers.ProfessionalCount))}+ professionals`,
            `Search ${formatNumberWithCommas(roundToNearest(statsNumbers.ProjectCount))}+ projects`,
        ];
    }, [statsNumbers]);

    const mobileProps = {
        autoFocus: true,
        hideSubmitButton: true,
        disableDropdown: true,
    };

    const { setCategory } = useCurrentCategory();

    // Set the current category for header navigation
    useEffect(() => {
        setCategory(null);
    }, [setCategory]);

    // Track the `SearchResults` event.
    useEffect(() => {
        const searchQuery = pageData?.searchQuery?.trim() ?? '';
        if (!searchQuery) {
            // Don't track search results if no query.
            lastSearchResultsTrackingKeyRef.current = null;
            return;
        }
        const currentPage = pageData?.pagination.page ?? 1;
        const totalResults = pageData?.totalCount;
        const filterStates = pageData?.filterStates;

        if (currentPage !== 1 || typeof totalResults !== 'number') {
            // Don't track search results if not first page.
            return;
        }
        const trackingKey = `${window.location.pathname}${window.location.search}`;
        if (trackingKey === lastSearchResultsTrackingKeyRef.current) {
            return;
        }
        trackSearchResults({
            tracker,
            query: searchQuery,
            totalResults,
            filterStates,
        });
        lastSearchResultsTrackingKeyRef.current = trackingKey;
    }, [
        pageData?.filterStates,
        pageData?.pagination.page,
        pageData?.searchQuery,
        pageData?.totalCount,
        tracker,
    ]);

    return (
        <div className="min-h-screen pb-20 md:min-h-auto md:pt-22">
            <div className="space-y-8 md:space-y-0">
                {shouldShowSearchBar && (
                    <div className="container mx-auto px-6 pt-3 md:max-w-[52.5rem] md:px-0 md:pt-[6.375rem] md:pb-22">
                        <div>
                            <div className="flex w-full">
                                <SearchBar
                                    key="searchpage-searchBar"
                                    ref={searchSectionInputRef}
                                    placeholderTexts={placeholderTexts}
                                    value={inputSearchQuery}
                                    isSearching={isSearching}
                                    onChange={handleInputChange}
                                    onKeyDown={handleKeyPress}
                                    onSearchSubmit={(value) => {
                                        if (typeof value === 'string') {
                                            handleSearch(value);
                                        }
                                    }}
                                    suggestionGroups={suggestionGroups}
                                    onSearchSuggestionClick={(suggestion) => {
                                        if (typeof suggestion === 'string') {
                                            trackingConfig.onSearchSuggestionClick(
                                                suggestion,
                                            );
                                            handleSearch(suggestion);
                                            addRecentSearch(suggestion);
                                        } else {
                                            // Object suggestion (professional or category) — navigate to detail page.
                                            trackingConfig.onSearchSuggestionClick(
                                                suggestion.title,
                                            );
                                            addRecentSearch(suggestion.title);
                                            navigate(suggestion.link);
                                        }
                                    }}
                                    clearSearchQuery={() => {
                                        searchSectionInputRef.current?.focus();
                                        clearSearchQuery();
                                    }}
                                    displayMode={appDisplayMode}
                                    variant={
                                        isMobile
                                            ? 'searchPageMobile'
                                            : 'default'
                                    }
                                    recentlySearched={recentlySearched}
                                    clearHistory={clearHistory}
                                    onRecentlySearchedClick={(search) => {
                                        trackingConfig.onRecentSearchClick(
                                            search,
                                        );
                                        addRecentSearch(search);
                                        navigate(
                                            generateURL('/search', {
                                                search,
                                            }),
                                        );
                                    }}
                                    {...(isMobile ? mobileProps : {})}
                                />
                            </div>
                        </div>
                    </div>
                )}
                <ClientOnly>
                    {() => (
                        <div>
                            {isMobile ? (
                                <SearchPageContentSectionsMobile
                                    view={view}
                                    filterSortProps={filterSortProps}
                                    suggestionGroups={suggestionGroups}
                                    inputSearchQuery={inputSearchQuery}
                                    isLoading={isResultsLoading}
                                />
                            ) : (
                                <SearchPageContentSectionsDesktop
                                    filterSortProps={filterSortProps}
                                    onSuggestedCategoryClick={(
                                        category: SearchSuggestion,
                                    ) => {
                                        trackingConfig.onSuggestedCategoryClick(
                                            category.title,
                                        );
                                    }}
                                    isLoading={isResultsLoading}
                                />
                            )}
                        </div>
                    )}
                </ClientOnly>
            </div>
        </div>
    );
}
